'use client';

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { useStore } from 'zustand';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import useIsMobile from '@/hooks/use-is-mobile.hook';
import { useOpenPaymentsAvailability } from '@/hooks/use-open-payments-availability.hook';
import { useOpenPayments } from '@/hooks/use-open-payments.hook';
import {
  markOpenPaymentsNoticeViewed,
  openPaymentsStore,
} from '@/store/open-payments/open-payments.store';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { OPEN_PAYMENTS_ROUTE } from '@/utils/consts/open-payments.consts';

export type OpenPaymentsNoticeVariant = 'banner' | 'popover';

export interface UseOpenPaymentsNoticeResult {
  isRendered: boolean;
  count: number;
}

export function useOpenPaymentsNotice(
  variant: OpenPaymentsNoticeVariant,
): UseOpenPaymentsNoticeResult {
  const { isAvailable } = useOpenPaymentsAvailability();
  const isDismissed = useStore(openPaymentsStore, (state) => state.isNoticeDismissed);
  const { count, hasPayments } = useOpenPayments();
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const isOpenPaymentsPage = pathname?.startsWith(OPEN_PAYMENTS_ROUTE) ?? false;

  const isVisible = isAvailable && hasPayments && !isDismissed && !isOpenPaymentsPage;
  const isRendered = isVisible && (variant === 'banner' ? isMobile : !isMobile);

  useEffect(() => {
    if (!isRendered || openPaymentsStore.getState().hasTrackedNoticeView) {
      return;
    }

    markOpenPaymentsNoticeViewed();
    getAnalyticsPlugin().track(AnalyticsEnum.VIEW_NOTICE, { qtd_pedidos_pendentes: count });
  }, [isRendered, count]);

  return { isRendered, count };
}
